import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { hydrateWhenVisible, cancelHydration, isInProductListingGrid } from '@theme/lazy-hydration';

const SECTION_ID_PREFIX = 'shopify-section-';
const DEFAULT_SECTION_ID = 'section-rendering-product-card';
const PRODUCT_CARD_SECTION_CACHE_MAX = 48;

/** @type {Map<string, string>} handle:sectionId → product-card innerHTML */
const productCardSectionCache = new Map();

/**
 * @param {string} handle
 * @param {string} sectionId
 */
function productCardCacheKey(handle, sectionId) {
  return `${handle}:${sectionId}`;
}

/**
 * @param {string} key
 * @param {string} html
 */
function cacheProductCardSection(key, html) {
  if (productCardSectionCache.has(key)) {
    productCardSectionCache.delete(key);
  }
  productCardSectionCache.set(key, html);
  while (productCardSectionCache.size > PRODUCT_CARD_SECTION_CACHE_MAX) {
    const oldest = productCardSectionCache.keys().next().value;
    if (oldest) productCardSectionCache.delete(oldest);
  }
}

/**
 * Placeholder HTML for product card while loading from AJAX.
 * @returns {string}
 */
function getPlaceholderHTML() {
  return `
    <a class="product-card__link product-card__placeholder-link" href="#" ref="productCardLink" aria-hidden="true" tabindex="-1">
      <span class="visually-hidden">Loading product</span>
      <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style product-card__placeholder">
        <div class="card-gallery card-gallery-default__media-wrapper product-card__placeholder-image" style="--gallery-aspect-ratio: 1;">
          <div class="product-media-container product-media-container--image card-gallery__placeholder">
            <span class="product-card__placeholder-shimmer" aria-hidden="true"></span>
          </div>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line"></span>
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-line--short"></span>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-price"></span>
        </div>
        <div class="product-card__placeholder-lines">
          <span class="product-card__placeholder-shimmer product-card__placeholder-line product-card__placeholder-line--button"></span>
        </div>
      </div>
    </a>
  `;
}

/**
 * A custom element that displays a product card.
 * When data-product-handle is set and content is empty, shows a placeholder and loads the card via Section Rendering API.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  /** @type {AbortController | null} */
  #loadAbortController = null;

  /** @type {IntersectionObserver | null} */
  #deferredLoadObserver = null;

  /** @type {string | null} */
  #committedBaseSlideId = null;

  /** @type {boolean} */
  #suspendGalleryHoverPreview = false;

  /** @returns {string[]} */
  static get observedAttributes() {
    return ['data-product-handle'];
  }

  /**
   * Rebuy/Vue templates bind `:data-product-handle` after mount; without this, `connectedCallback`
   * runs with no handle, no placeholder is injected, and `getProductCardLink()` stays null.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-handle' || oldValue === newValue || !newValue) return;

    this.#syncPlaceholderAndRefs();
    this.updatedCallback?.();
    this.#finalizeCardConnection();
  }

  /**
   * True when this card is in placeholder state (data-product-handle, no real link yet).
   * @returns {boolean}
   */
  #isAjaxPlaceholder() {
    if (!this.hasAttribute('data-product-handle')) return false;
    if (this.hasAttribute('data-load-from-ajax')) return true;
    const href = this.querySelector('a[ref="productCardLink"]')?.getAttribute('href') ?? '';
    const noMeaningfulHref = !href || href === '#' || href === '';
    return noMeaningfulHref;
  }

  get productPageUrl() {
    return this.getProductCardLink()?.href ?? '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.querySelector('a[ref="productCardLink"]') || null;
  }

  #fetchProductPageHandler = () => {
    const quickAdd = /** @type {HTMLElement & { fetchProductPage?: (url: string) => Promise<Document | null> } | null} */ (this.querySelector('quick-add'));
    quickAdd?.fetchProductPage?.(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /**
   * Injects AJAX placeholder markup and relaxes required refs when in placeholder mode.
   */
  #syncPlaceholderAndRefs() {
    const productHandle = this.getAttribute('data-product-handle');
    const loadFromAjax = this.hasAttribute('data-load-from-ajax');
    const productCardLink = this.querySelector('a[ref="productCardLink"]');
    const hasContent = productCardLink?.getAttribute('href')?.replace(/^#\/?$/, '');
    const isAjaxMode = Boolean(productHandle) && (loadFromAjax || !hasContent);

    if (isAjaxMode && !this.querySelector('.product-card__placeholder')) {
      this.innerHTML = getPlaceholderHTML();
    }
    if (this.#isAjaxPlaceholder()) {
      this.requiredRefs = [];
    }
  }

  /**
   * Starts section fetch when the card still has only a placeholder (or missing) link, or wires
   * full card behaviour when real markup is present.
   */
  #finalizeCardConnection() {
    const productHandle = this.getAttribute('data-product-handle');
    const link = this.getProductCardLink();
    const hrefAttr = link?.getAttribute('href');
    const needsAjaxLoad =
      Boolean(productHandle) &&
      (!(link instanceof HTMLAnchorElement) || !hrefAttr || hrefAttr === '#' || hrefAttr === '');

    if (needsAjaxLoad && productHandle) {
      if (this.#shouldDeferAjaxLoad()) {
        this.#scheduleDeferredAjaxLoad(productHandle);
      } else {
        this.#loadProductCardFromAjax(productHandle);
      }
      return;
    }

    if (!(link instanceof HTMLAnchorElement)) return;
    this.#initContent();
  }

  connectedCallback() {
    this.#syncPlaceholderAndRefs();
    super.connectedCallback();
    this.#finalizeCardConnection();
  }

  /**
   * Initializes event listeners and behaviour when the card has real content (server-rendered or after AJAX load).
   */
  #initContent() {
    this.#handleQuickAdd();
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
    this.addEventListener('click', this.navigateToProduct);
    this.#syncCommittedBaseToInitialSlide();
    const schedulePreload = () => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    };
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(schedulePreload, { timeout: 2500 });
    } else {
      setTimeout(schedulePreload, 200);
    }
  }

  /**
   * Defer section fetch for drawer/Rebuy rails until the card is near the viewport.
   * @returns {boolean}
   */
  #shouldDeferAjaxLoad() {
    if (!this.hasAttribute('data-load-from-ajax')) return false;
    return Boolean(this.closest('.cart-drawer, .rebuy-product-slider'));
  }

  /**
   * @param {string} productHandle
   */
  #scheduleDeferredAjaxLoad(productHandle) {
    this.#deferredLoadObserver?.disconnect();
    this.#deferredLoadObserver = null;

    if (typeof IntersectionObserver !== 'function') {
      this.#loadProductCardFromAjax(productHandle);
      return;
    }

    const scrollRoot = this.closest('.cart-drawer__content, scroll-hint');
    this.#deferredLoadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.#deferredLoadObserver?.disconnect();
        this.#deferredLoadObserver = null;
        this.#loadProductCardFromAjax(productHandle);
      },
      {
        root: scrollRoot instanceof Element ? scrollRoot : null,
        rootMargin: '120px 0px',
        threshold: 0,
      }
    );
    this.#deferredLoadObserver.observe(this);
  }

  /**
   * @param {string} cardContent
   */
  #applyLoadedCardContent(cardContent) {
    this.innerHTML = cardContent;
    this.requiredRefs = ['productCardLink'];
    this.updatedCallback?.();
    this.#updateRefsForAjaxLoad();
    this.#initContent();
  }

  /**
   * Loads product card HTML via Section Rendering API and replaces placeholder.
   * @param {string} productHandle - Product handle for the URL.
   */
  async #loadProductCardFromAjax(productHandle) {
    const sectionId = this.getAttribute('data-section-id') || DEFAULT_SECTION_ID;
    const cacheKey = productCardCacheKey(productHandle, sectionId);
    const cached = productCardSectionCache.get(cacheKey);
    if (cached) {
      this.#applyLoadedCardContent(cached);
      return;
    }

    const productPath = `products/${productHandle}`;
    const url = new URL(productPath, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    this.#loadAbortController?.abort();
    this.#loadAbortController = new AbortController();

    try {
      const response = await fetch(url.toString(), { signal: this.#loadAbortController.signal });
      if (!response.ok) {
        throw new Error(`Product card fetch failed: ${response.status}`);
      }
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const normalizedId = sectionId.replace(/^shopify-section-/, '');
      const sectionEl =
        doc.getElementById(`${SECTION_ID_PREFIX}${normalizedId}`) ??
        doc.querySelector('.shopify-section') ??
        doc.body?.firstElementChild;
      const productCard = sectionEl?.querySelector('product-card');
      const cardContent = productCard?.innerHTML;
      if (!cardContent?.trim()) {
        throw new Error('Product card content not found in section response');
      }
      cacheProductCardSection(cacheKey, cardContent);
      this.#applyLoadedCardContent(cardContent);
    } catch (err) {
      console.warn('loadProductCardFromAjax failed:', err.message);
      if (err?.name === 'AbortError') return;
      this.#showPlaceholderError();
    } finally {
      this.#loadAbortController = null;
    }
  }

  /**
   * Refreshes refs after AJAX content replace (base class #updateRefs is private, so we trigger it via DOM).
   */
  #updateRefsForAjaxLoad() {
    if (typeof this.updatedCallback === 'function') {
      this.updatedCallback();
    }
  }

  /**
   * Shows an error state in the placeholder (e.g. failed to load).
   */
  #showPlaceholderError() {
    const link = this.querySelector('.product-card__placeholder-link');
    if (link) {
      link.innerHTML = `
        <span class="visually-hidden">Failed to load product</span>
        <div class="product-card__content product-card__placeholder product-card__placeholder--error">
          <p class="product-card__placeholder-error-text">Unable to load product</p>
        </div>
      `;
    }
  }

  disconnectedCallback() {
    this.#deferredLoadObserver?.disconnect();
    this.#deferredLoadObserver = null;
    this.#loadAbortController?.abort();
    this.#loadAbortController = null;
    cancelAnimationFrame(this.#hoverResetFrame);
    this.#hoverResetFrame = 0;
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  #syncCommittedBaseToInitialSlide() {
    let { slideshow } = this.refs;
    if (!slideshow) {
      const slideshowEl = this.querySelector('[ref="slideshow"]');
      if (slideshowEl) {
        this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
      }
      slideshow = this.refs.slideshow;
    }

    if (!slideshow) return;

    const initialSlideId = slideshow.initialSlide?.getAttribute('slide-id');
    if (initialSlideId) {
      this.#committedBaseSlideId = initialSlideId;
    }
  }

  #setCommittedBaseSlideId(id) {
    if (typeof id === 'string' && id.length > 0) {
      this.#committedBaseSlideId = id;
      return;
    }

    this.#syncCommittedBaseToInitialSlide();
  }

  #getCommittedBaseSlideId(slideshow) {
    const visibleSlides = slideshow.slides ?? [];

    if (this.#committedBaseSlideId) {
      const matchingVisibleSlide = visibleSlides.find(
        (slide) => slide.getAttribute('slide-id') === this.#committedBaseSlideId
      );

      if (matchingVisibleSlide) {
        return this.#committedBaseSlideId;
      }
    }

    const initialSlideId = slideshow.initialSlide?.getAttribute('slide-id');
    if (initialSlideId) {
      this.#committedBaseSlideId = initialSlideId;
      return initialSlideId;
    }

    return null;
  }

  #restoreCommittedBaseSlide(slideshow) {
    const committedBaseSlideId = this.#getCommittedBaseSlideId(slideshow);
    if (!committedBaseSlideId) return false;

    slideshow.select({ id: committedBaseSlideId }, undefined, { animate: false });
    return true;
  }

  /**
   * Gets the explicit hover-preview slide index.
   * Uses the first non-hidden slide after the committed base slide so hover previews stay
   * relative to the active base image (initial card image or clicked swatch image).
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {number | null}
   */
  #getHoverPreviewIndex(slideshow) {
    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return null;

    const committedBaseSlideId = this.#getCommittedBaseSlideId(slideshow);
    const committedBaseSlide = committedBaseSlideId
      ? slides.find((slide) => slide.getAttribute('slide-id') === committedBaseSlideId)
      : null;
    const baseIndex = committedBaseSlide ? slides.indexOf(committedBaseSlide) : 0;

    for (let index = baseIndex + 1; index < slides.length; index += 1) {
      if (!slides[index].hasAttribute('hidden')) return index;
    }

    return null;
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    // event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    const quickAdd = /** @type {HTMLElement & { fetchProductPage?: (url: string) => Promise<Document | null> } | null} */ (this.querySelector('quick-add'));
    quickAdd?.fetchProductPage?.(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#setCommittedBaseSlideId(this.variantPicker?.selectedOption?.dataset.optionMediaId ?? null);
    this.#suspendGalleryHoverPreview = false;
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();

    // Ref may have been replaced by morph; refresh slideshow ref
    const slideshowEl = this.querySelector('[ref="slideshow"]');
    if (slideshowEl) {
      this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
    }
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {number} */
  #hoverResetFrame = 0;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#suspendGalleryHoverPreview = true;
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#suspendGalleryHoverPreview) return;

    let { slideshow } = this.refs;
    if (!slideshow) {
      const slideshowEl = this.querySelector('[ref="slideshow"]');
      if (slideshowEl) {
        this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
      }
      slideshow = this.refs.slideshow;
    }

    if (!slideshow) return;

    this.resetVariant.cancel();

    /**
     * Card gallery uses on:pointerenter → previewImage before the pointer is always inside
     * slideshow-component. Adjacent slides use content-visibility: auto until [actioned] is set
     * (see base.css .card-gallery … slideshow-component[actioned]). Slideshow only sets
     * [actioned] on its own pointerenter, so the second image could appear only after mousemove.
     */
    slideshow.setAttribute('actioned', '');
    const previewIndex = this.#getHoverPreviewIndex(slideshow);
    if (previewIndex == null) return;

    slideshow.select(previewIndex, undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    cancelAnimationFrame(this.#hoverResetFrame);
    this.#hoverResetFrame = requestAnimationFrame(() => {
      this.#hoverResetFrame = 0;

      const gallery = this.querySelector('card-gallery');
      if (gallery?.matches(':hover')) return;

      let { slideshow } = this.refs;
      if (!slideshow) {
        const slideshowEl = this.querySelector('[ref="slideshow"]');
        if (slideshowEl) {
          this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
        }
        slideshow = this.refs.slideshow;
      }

      if (!slideshow) return;

      this.#suspendGalleryHoverPreview = false;

      if (this.#restoreCommittedBaseSlide(slideshow)) {
        return;
      }

      this.resetVariant();
    });
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#restoreCommittedBaseSlide(slideshow)) {
      return;
    }

    // No valid committed base slide — step back one slide (not Slideshow#previous, which can skip)
    const cur = slideshow.current;
    if (cur > 0) {
      slideshow.select(cur - 1, undefined, { animate: false });
    }
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.getProductCardLink();
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    // Wiring a picker costs two hover listeners per swatch, so a full page of results can register
    // hundreds of listeners before a shopper has seen any of them. Set each grid picker up as its
    // card approaches the viewport instead, which is well before it can be interacted with.
    if (!isInProductListingGrid(this)) {
      this.#connect();
      return;
    }

    hydrateWhenVisible(this, () => {
      if (this.isConnected) this.#connect();
    });
  }

  disconnectedCallback() {
    cancelHydration(this);
    super.disconnectedCallback();
  }

  #connect() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard && this.parentProductCard.getProductCardLink() !== null) {
      const currentUrl = new URL(this.parentProductCard.getProductCardLink().href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.getProductCardLink().href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }



  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
